import { Trash } from '@phosphor-icons/react';
import { type ElementRef, useCallback, useEffect, useRef, useState } from 'react';
import translations from '../../../ts/translations';
import { type UserApiKey, deleteKey, getKeys, postKey } from '../../sources/user';
import type { UserTenantResponseAPI } from '../../types/user';
import { getByID } from '../../utils/collection';
import { isNullOrWhitespace } from '../../utils/guard';
import { formatTenantName } from '../../utils/tenant';
import CopyableText from '../generic/CopyableText';
import FormGroup from '../generic/FormGroup';
import ShowHide from '../generic/ShowHide';
import Table, { type TableColumnList } from '../generic/Table';
import TenantName from '../generic/TenantName';
import ConfirmModal from '../generic/modal/ConfirmModal';
import { useNotificationDispatch } from '../notifications/NotificationProvider';
import './userSettings.less';
import { useUser } from '../UserProvider';
import { useIsSingleTenant } from '../../hooks/organization';

const sortApiKeys = (apiKeys: UserApiKey[]) => {
    return apiKeys.slice().sort((first, second) => {
        // Subtract second date from first to get ascending sort order
        return new Date(second.createdAt).getTime() - new Date(first.createdAt).getTime();
    });
};

const sortTenants = (tenants: UserTenantResponseAPI[]) => {
    return tenants.slice().sort((first, second) => {
        const firstLowerCase = first.developerName.toLowerCase();
        const secondLowerCase = second.developerName.toLowerCase();

        if (firstLowerCase < secondLowerCase) {
            return -1;
        }

        if (firstLowerCase > secondLowerCase) {
            return 1;
        }

        return 0;
    });
};

const UserSettings = ({ tenant }: { tenant: UserTenantResponseAPI }) => {
    const [APIKeys, setAPIKeys] = useState<UserApiKey[]>([]);
    const [APIKeysLoading, setAPIKeysLoading] = useState(false);
    const [selectedTenantId, setSelectedTenantId] = useState('');
    const [keyName, setKeyName] = useState('');
    const [selectedKey, setSelectedKey] = useState<UserApiKey | null>(null);
    const [showConfirmRemoveKey, setShowConfirmRemoveKey] = useState(false);
    const isSingleTenant = useIsSingleTenant(tenant);
    const modalContainerRef = useRef<ElementRef<'div'>>(null);

    const dispatchNotification = useNotificationDispatch();
    const user = useUser();

    const fetchAPIKeys = useCallback(async () => {
        try {
            const keys = await getKeys();

            const sortedKeys = sortApiKeys(keys);

            setAPIKeys(sortedKeys);
            setAPIKeysLoading(false);
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error,
                },
            });
        }
    }, [dispatchNotification]);

    useEffect(() => {
        fetchAPIKeys();
    }, [fetchAPIKeys]);

    // When the organization has a single tenant there is nothing to choose,
    // so auto-select it and hide the tenant selector.
    useEffect(() => {
        if (isSingleTenant) {
            setSelectedTenantId(tenant.id);
        }
    }, [isSingleTenant, tenant.id]);

    if (!(user?.tenants && user.id && user.email)) {
        return null;
    }

    const { tenants, id, email } = user;

    const onGenerateKey = async () => {
        if (isNullOrWhitespace(keyName) || isNullOrWhitespace(selectedTenantId)) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error: translations.USER_SETTINGS_api_key_validation_message,
                },
            });
            return;
        }

        try {
            await postKey(keyName, selectedTenantId);
            dispatchNotification({
                type: 'notifySuccess',
                payload: {
                    message: translations.USER_SETTINGS_api_generate_success_message,
                },
            });

            await fetchAPIKeys();

            setKeyName('');
            setSelectedTenantId('');
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error,
                },
            });
        }
    };

    const onRemoveKey = async () => {
        if (!selectedKey) {
            dispatchNotification({
                type: 'addNotification',
                payload: {
                    type: 'info',
                    message: translations.USER_SETTINGS_no_api_key_selected_message,
                    isPersistent: false,
                },
            });
            return;
        }

        try {
            await deleteKey(selectedKey.name);

            dispatchNotification({
                type: 'notifySuccess',
                payload: {
                    message: translations.USER_SETTINGS_api_delete_success_message,
                },
            });

            await fetchAPIKeys();

            setShowConfirmRemoveKey(false);
            setSelectedKey(null);
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error,
                },
            });
        }
    };

    const APIKeysColumns: TableColumnList<UserApiKey> = [
        {
            renderHeader: () => translations.COMMON_TABLE_key_name,
            renderCell: ({ item }) => item.name,
        },
        {
            renderHeader: () => translations.COMMON_TABLE_tenant_name,
            renderCell: ({ item }) => (
                <TenantName name={getByID(item.tenantId, tenants)?.developerName ?? ''} />
            ),
        },
        {
            renderHeader: () => translations.COMMON_TABLE_tenant_id,
            renderCell: ({ item }) => item.tenantId,
        },
        {
            renderHeader: () => translations.COMMON_TABLE_created_at_utc,
            renderCell: ({ item }) =>
                new Date(item.createdAt).toLocaleString(undefined, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                }),
        },
        {
            renderHeader: () => translations.COMMON_TABLE_key,
            renderCell: ({ item }) => (
                <ShowHide
                    renderTrigger={(isShowing) =>
                        !isShowing && (
                            <span className="link-emulate">
                                {translations.USER_SETTINGS_show_api_key_button_label}
                            </span>
                        )
                    }
                >
                    <CopyableText copyableText={item.apiKey} hasCopyButton={true} />
                </ShowHide>
            ),
            size: '400px',
        },
        {
            renderHeader: () => translations.COMMON_TABLE_actions,
            renderCell: ({ item }) => (
                <div className="action-btn-wrapper">
                    <button
                        title={translations.USER_SETTINGS_delete_api_key_button_label}
                        className="table-icon table-icon-delete"
                        aria-label={translations.USER_SETTINGS_delete_api_key_button_label}
                        onClick={() => {
                            setShowConfirmRemoveKey(true);
                            setSelectedKey(item);
                        }}
                        type="button"
                    >
                        <Trash />
                    </button>
                </div>
            ),
            size: '5rem',
        },
    ];

    return (
        <span className="admin-transition">
            <div className="flex-column full-height">
                <div className="admin-page" ref={modalContainerRef}>
                    <h1>{translations.USER_SETTINGS_heading}</h1>
                    <CopyableText copyableText={id} labelText="User ID: " hasCopyButton={true} />
                    <CopyableText copyableText={email} labelText="Email: " hasCopyButton={true} />
                    <h2>{translations.USER_SETTINGS_api_keys_heading}</h2>
                    <p>{translations.USER_SETTINGS_api_key_description}</p>

                    <h3>{translations.USER_SETTINGS_generate_api_key_heading}</h3>
                    <div className="flex gap-small" onSubmit={onGenerateKey}>
                        <FormGroup
                            className="user-settings-form-group"
                            isRequired
                            htmlFor="key-name-input"
                            label={translations.USER_SETTINGS_api_key_name_input_label}
                        >
                            <input
                                id="key-name-input"
                                value={keyName}
                                onChange={({ target: { value } }) => setKeyName(value)}
                                className="form-control"
                            />
                        </FormGroup>
                        {!isSingleTenant && (
                            <FormGroup
                                className="user-settings-form-group"
                                isRequired
                                htmlFor="tenant-select"
                                label={translations.USER_SETTINGS_tenant_select_label}
                            >
                                <select
                                    id="tenant-select"
                                    className="form-control form-select"
                                    value={selectedTenantId || ''}
                                    onChange={({ target: { value } }) => setSelectedTenantId(value)}
                                >
                                    {
                                        <option value="" key="please-select">
                                            {
                                                translations.USER_SETTINGS_tenant_select_default_value_label
                                            }
                                        </option>
                                    }
                                    {sortTenants(tenants).map((tenant) => {
                                        return (
                                            <option value={tenant.id} key={tenant.id}>
                                                {formatTenantName(tenant.developerName)}
                                            </option>
                                        );
                                    })}
                                </select>
                            </FormGroup>
                        )}
                        <button
                            type="button"
                            className="btn btn-primary flex-end generate-key-button"
                            onClick={onGenerateKey}
                        >
                            {translations.USER_SETTINGS_generate_api_key_button_label}
                        </button>
                    </div>
                    <Table
                        wrapperClassName="margin-top"
                        columns={APIKeysColumns}
                        items={APIKeys}
                        pagination={true}
                        isLoading={APIKeysLoading}
                        rowKeyGenerator={(item) => `${item.name}-${item.tenantId}`}
                    />
                    <ConfirmModal
                        title={translations.USER_SETTINGS_api_key_remove_confirm_title}
                        messages={[translations.USER_SETTINGS_key_remove_confirm_message]}
                        show={showConfirmRemoveKey}
                        onConfirm={onRemoveKey}
                        onCancel={() => {
                            setShowConfirmRemoveKey(false);
                            setSelectedKey(null);
                        }}
                        buttonStyle="danger"
                        buttonCaption={
                            translations.USER_SETTINGS_confirm_delete_api_key_button_label
                        }
                        container={modalContainerRef.current}
                    />
                </div>
            </div>
        </span>
    );
};

export default UserSettings;
